import React, { type FC, useMemo } from 'react'

import { useAtom } from 'jotai'
import { GridCell, Select } from 'mailup-web-components'
import { type IntlShape, useIntl } from 'react-intl'

import { type UiEngagementSpec } from '../../filterConditionTypes'
import { filterFormImmerAtom } from '../../state/state'
import { type ConditionProps } from '../../types'
import EngagementOption from './EngagementOption'
import EngagementSelectedOption from './EngagementSelectedOption'

const MemoEngagementOption = React.memo(
  EngagementOption,
  (prevProps, nextProps) => {
    return (
      prevProps.focus === nextProps.focus &&
      prevProps.loading === nextProps.loading &&
      prevProps.optionIndex === nextProps.optionIndex &&
      prevProps.option.value === nextProps.option.value
    )
  },
)

const getEngagementOptions = (
  intl: IntlShape,
): Array<{
  value: UiEngagementSpec['Value']
  label: string
}> => {
  return [
    {
      label: intl.formatMessage({
        id: 'Load.Filters.EngagementScoreVeryActive',
      }),
      value: 'VeryActive',
    },
    {
      label: intl.formatMessage({ id: 'Load.Filters.EngagementScoreActive' }),
      value: 'Active',
    },
    {
      label: intl.formatMessage({
        id: 'Load.Filters.EngagementScoreLessActive',
      }),
      value: 'LessActive',
    },
    {
      label: intl.formatMessage({
        id: 'Load.Filters.EngagementScoreInactive',
      }),
      value: 'Inactive',
    },
    //
    {
      label: intl.formatMessage({
        id: 'Load.Filters.EngagementScoreNewNever',
      }),
      value: 'New',
    },
    {
      label: intl.formatMessage({
        id: 'Load.Filters.EngagementScoreExcluded',
      }),
      value: 'Abandoned',
    },
  ]
}

const getEngagementOptionGroups = (
  intl: IntlShape,
): Array<{
  label: string
  values: Array<UiEngagementSpec['Value']>
}> => {
  return [
    {
      label: intl.formatMessage({ id: 'Load.Filters.EngagementWhoYouSent' }),
      values: ['VeryActive', 'Active', 'LessActive', 'Inactive'],
    },
    {
      label: intl.formatMessage({
        id: 'Load.Filters.EngagementWhoNotYouSent',
      }),
      values: ['New', 'Abandoned'],
    },
  ]
}

const EngagementSpecCondition: FC<ConditionProps> = ({
  blockIndex,
  condIndex,
}) => {
  const intl = useIntl()

  const [filter, setFilter] = useAtom(filterFormImmerAtom)
  const condition = filter.Blocks[blockIndex].Conditions[condIndex]
  const spec = condition.Spec as UiEngagementSpec

  const options = useMemo(() => getEngagementOptions(intl), [intl])
  const optionGroups = useMemo(() => getEngagementOptionGroups(intl), [intl])

  const handleChange = (v: UiEngagementSpec['Value']): void => {
    setFilter((draft) => {
      const draftSpec = draft.Blocks[blockIndex].Conditions[condIndex]
        .Spec as UiEngagementSpec
      draftSpec.Value = v
    })
  }

  return (
    <GridCell span={5}>
      <Select
        OptionComponent={MemoEngagementOption}
        SelectedItemComponent={EngagementSelectedOption}
        searchable={false}
        value={spec.Value}
        options={options}
        optionGroups={optionGroups}
        onChange={handleChange}
        placeholder={intl.formatMessage({
          id: 'Load.Filters.SelectCondition',
        })}
        data-mu-test='input-engagement-score'
      />
    </GridCell>
  )
}

export default EngagementSpecCondition
